import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { Utilities } from '@app/common/utilities';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { ChannelType, UserChannelTerm } from '@app/models/user-channel-term';
import { BroadcastService } from '@app/services/broadcast.service';
import { CacheService } from '@app/services/cache.service';
import { ChannelService } from '@app/services/channel.service';
import { RestaurantMenuService } from '@app/services/restaurant-menu.service';
import { RestaurantService } from '@app/services/restaurant.service';
import { UserService } from '@app/services/user.service';
import { AppTileService } from '@app/services/app-tile.service';
import { NewsService } from '@app/services/news.service';
import { UserAppTileService } from '@app/services/user-app-tile.service';
import { GroupsService } from '@app/services/groups.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { addHours } from 'date-fns';
import { concat, forkJoin, iif, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserChannelTermsService {

  constructor(private http: HttpClient,
              private cache: CacheService,
              private httpHelper: HttpHelper,
              private snackBar: MatSnackBar,
              private newsService: NewsService,
              private userAppTileService: UserAppTileService,
              private appTileService: AppTileService,
              private channelService: ChannelService,
              private broadcastService: BroadcastService,
              private restaurantService: RestaurantService,
              private restaurantMenuService: RestaurantMenuService,
              private userService: UserService,
              private groupsService: GroupsService,
              private featureSettingsStoreService: FeatureSettingsStoreService) { }

  private readonly cacheKey = 'UserChannelTermsService-getUserChannelTerms';
  public useCache = true;
  private getUserChannelTerms$: Observable<Array<UserChannelTerm>>;
  private reload$ = new Subject<void>();

  reload() {
    this.useCache && this.cache.removeFromPersistentCache(this.cacheKey);
    this.getUserChannelTerms$ = null;
    this.reload$.next();

    this.newsService.reloadAllPersonalNews();
    this.newsService.reloadAllTopNews();
    this.userAppTileService.reload();
    this.appTileService.reload();
    this.broadcastService.reload();
    this.restaurantService.reload();
    this.restaurantMenuService.reload();
  }

  getUserChannelTerms(): Observable<Array<UserChannelTerm>> {
    if (this.getUserChannelTerms$) {
      return this.getUserChannelTerms$;
    } else {
      this.getUserChannelTerms$ = this.http.get<Array<UserChannelTerm>>(`${Constants.API_ENDPOINT}/me/userchannelterms/`)
        .pipe(
          map(response => {
            return response.map(p => new UserChannelTerm(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(this.cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getUserChannelTerms', result: new Array<UserChannelTerm>(), rethrow: true })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<UserChannelTerm>>(this.cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getUserChannelTerms$);
        }
      }
      return this.getUserChannelTerms$;
    }
  }

  add(personalization: UserChannelTerm, doReload = true): Observable<any> {
    return this.http.post<Array<UserChannelTerm>>(`${Constants.API_ENDPOINT}/me/userchannelterms`, personalization)
      .pipe(
        tap(() => {
          if (doReload) {
            this.reload();
          }
        }),
        catchError(this.httpHelper.handleError({ operation: 'add', result: {}, rethrow: true })));
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${Constants.API_ENDPOINT}/me/userchannelterms/${id}`, {

    })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'delete', result: {}, rethrow: true })));
  }

  syncUserChannelsWithAD(currentRouteUrl: string): Observable<any> {
    const pushedFromAdPersonalization = new Array<UserChannelTerm>();
    const alreadyPushedCacheKey = `${this.cacheKey}-alreadyPushed`;
    const alreadyPushed = this.cache.get<boolean>(alreadyPushedCacheKey); // use this to make sure we only try to do the push/sync operation once per 24h
    const requestedPath = this.cache.getWithCustomKey<string>(Constants.WANTED_ROUTE_PATH);
    if (currentRouteUrl.endsWith('settings') || requestedPath?.endsWith('settings')) {
      return of(null);
    }

    if (!alreadyPushed) {

      const userGroups$ = iif(() => this.featureSettingsStoreService.state.enableAzureADGroupSync,
      this.groupsService.getAllMyTransitiveMemeberGroups()
      .pipe(
        map((groups) => {
          if (groups?.length) {
            return groups;
          } else {
            return null;
          }
        })
      ), of(null));

      return forkJoin(
        [
          this.channelService.getChannels(true),
          this.userService.getMyDetailsFromMsGraph(true),
          this.getUserChannelTerms(),
          userGroups$,
        ])
        .pipe(
          mergeMap(([channels, myDetails, userChannelTerms, userGroups]) => {

            channels.forEach(c => {
              const channel = new SimplifiedTerm(c);
              let azureADUserProperty: string;
              const mappingProperty = channel?.LocalCustomProperties[Constants.SETTINGS_GRAPH_API_PERSONALIZATION_MAPPING_KEY];

              if (mappingProperty && myDetails) {
                const mappingPropertyKey = Object.keys(myDetails).find(key => key.toLowerCase() === mappingProperty.toLowerCase());
                if (mappingPropertyKey) {
                  azureADUserProperty = myDetails[mappingPropertyKey];
                }
              }

              if (channel.Terms) {
                channel.Terms.forEach(term => {
                  this.setSelectedTerms(term, userChannelTerms, azureADUserProperty, userGroups, pushedFromAdPersonalization);
                });
              }
            });

            this.cache.set(alreadyPushedCacheKey, true, addHours(new Date(), 24));

            if (pushedFromAdPersonalization.length > 0 && !alreadyPushed) {
              const personalizationsToAdd = pushedFromAdPersonalization.map(p => this.add(p, false));
              const concatenatedAdds = concat(...personalizationsToAdd);
              return forkJoin([concatenatedAdds]);
            } else {
              return of(null);
            }
          }),
          tap((result) => {
            if (result) {
              Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.interestsselectionsynced);
            }
            this.reload();
          })
        );
    } else {
      return of(null);
    }
  }

  syncUserChannels(currentRouteUrl: string): Observable<any> {
    let termsToDelete = new Array <UserChannelTerm>();
    const alreadySyncedCacheKey = `${this.cacheKey}-alreadySyncedChannels`;
    const alreadySynced = this.cache.get<boolean>(alreadySyncedCacheKey); // use this to make sure we only try to do the push/sync operation once per 24h
    const requestedPath = this.cache.getWithCustomKey<string>(Constants.WANTED_ROUTE_PATH);
    if (currentRouteUrl.endsWith('settings') || requestedPath?.endsWith('settings')) {
      return of(null);
    }

    if (!alreadySynced) {
      return forkJoin(
        [
          this.channelService.getChannels(true),
          this.getUserChannelTerms()
        ])
        .pipe(
          mergeMap(([channels, userChannelTerms]) => {
            const flattenChannels = channels.flattenRecursive('Terms');

            // check so that we get channels from SharePoint/Api
            if (flattenChannels.length > 0) {
              termsToDelete = userChannelTerms.filter(p => !flattenChannels.some(c => c.Guid === p.ChannelTermId));
            }

            this.cache.set(alreadySyncedCacheKey, true, addHours(new Date(), 24));

            if (termsToDelete.length > 0 && !alreadySynced) {
              const deleteObservables = termsToDelete.map(term =>
                this.delete(term.ChannelTermId)
              );
              return forkJoin(deleteObservables);
            } else {
              return of(null);
            }

          }),
          tap((result) => {
            if (result) {
              Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.interestsselectionsynced);
            }
            this.reload();
          })
        );
    } else {
      return of(null);
    }
  }

  private setSelectedTerms(term: SimplifiedTerm, userChannelTerms: UserChannelTerm[], userProfileProperty: string, userGroups: string[], pushedFromAdPersonalization: UserChannelTerm[]) {
    // if term has sub terms, recursively run this function on them first
    if (term.Terms) {
      term.Terms.forEach(t => {
        this.setSelectedTerms(t, userChannelTerms, userProfileProperty, userGroups, pushedFromAdPersonalization);
      });
    }
    // check personalizations (settings in database) to see if this should be selected
    const personalization = userChannelTerms.find(p => p.ChannelTermId === term.Guid);
    if (personalization) {
      term.Selected = true;
    }

    let azureADGroupMatch: boolean;
    let matchingProperty: boolean;

    // check if users groups match with channel
    if (userGroups) {
      const azureADGroupMappingKeys: string[] = term.LocalCustomProperties[Constants.SETTINGS_AZURE_AD_GROUP_MAPPING_KEYS] ? term.LocalCustomProperties[Constants.SETTINGS_AZURE_AD_GROUP_MAPPING_KEYS].split(',').map(key => key.trim()) : null;
      if (azureADGroupMappingKeys) {
        azureADGroupMatch = azureADGroupMappingKeys.some(id => userGroups.some(groupId => groupId.toLowerCase() === id.toLowerCase()));
      }
    }

    // Do not check if azureADGroupMatch is true. The channel will be pushed either way
    // check for channels pushed from AD to this user
    if (!azureADGroupMatch && userProfileProperty) {
      matchingProperty = term.Name.replace(/＆/g, '&').toLowerCase() === userProfileProperty.toLowerCase();
      if (!matchingProperty) {
        if (!term.Labels) {
          matchingProperty = false;
        } else {
          matchingProperty = term.Labels.some(l => l.Name.replace(/＆/g, '&').toLowerCase() === userProfileProperty.toLowerCase());
        }
      }
    }

    if (azureADGroupMatch || matchingProperty) {
      term.DisableSelection = true;
      if (!term.Selected) {
        term.Selected = true;

        // channel will be pushed to db if not exists
        if (!personalization) {
          if (!pushedFromAdPersonalization.find(p => p.ChannelTermId === term.Guid) && !userChannelTerms.find(p => p.ChannelTermId === term.Guid)) {
            pushedFromAdPersonalization.push(new UserChannelTerm({ ChannelTermId: term.Guid, ChannelType: ChannelType.FromAd }));
          }
        }
      }
    }

    // selected should traverse upwards, to check child terms if any is selected and set this too in that case
    if (term.Terms) {
      const pushedTerms = term.Terms.filter(t => {
        return t.Selected && t.DisableSelection;
      });
      if (pushedTerms.length > 0) {
        term.Selected = true;
        term.DisableSelection = true;
        if (!pushedFromAdPersonalization.find(p => p.ChannelTermId === term.Guid) && !userChannelTerms.find(p => p.ChannelTermId === term.Guid)) {
          pushedFromAdPersonalization.push(new UserChannelTerm({ ChannelTermId: term.Guid, ChannelType: ChannelType.FromAd }));
        }
      }
    }
  }
}
